import { ChevronDown, ChevronUp, Clock, MapPin } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import { typography } from '../../styles/typography';
import { getAllSites } from '../SiteManagement/siteApi';
import { Card, Input, PrimaryButton, SearchBar } from '../ui';
import { restaurantApi } from './restaurantApi';

const combineClasses = (...classes) => classes.filter(Boolean).join(' ');

// Helper function to format received_at timestamp
const formatReceivedAt = (receivedAt) => {
  if (!receivedAt) {
    return new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }
  
  // If it's already a formatted time string (HH:MM), return as-is
  if (typeof receivedAt === 'string' && /^\d{1,2}:\d{2}/.test(receivedAt)) {
    return receivedAt;
  }
  
  // If it's a timestamp (ISO string or number), format it
  try {
    const date = new Date(receivedAt);
    if (!isNaN(date.getTime())) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    }
  } catch (e) {
    console.warn('Error formatting received_at:', receivedAt, e);
  }
  
  // Fallback to current time
  return new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

// Dummy KOT data – replace with API when available
// const DUMMY_KOT_ORDERS = [
//   {
//     id: 'kot-1',
//     orderNumber: 1042,
//     table: 12,
//     section: 'Main Hall',
//     receivedAt: '14:32',
//     status: 'new',
//     items: [
//       { name: 'Shrimp Basil Salad', qty: 1, note: 'No onions' },
//       { name: 'Chicken Burger', qty: 2, note: 'Extra mayo' },
//       { name: 'Iced Coffee', qty: 2, note: 'Less ice' },
//     ],
//   },
//   {
//     id: 'kot-2',
//     orderNumber: 1043,
//     table: 5,
//     section: 'Terrace',
//     receivedAt: '14:28',
//     status: 'new',
//     items: [
//       { name: 'Margherita Pizza', qty: 1, note: 'Well done' },
//       { name: 'Garlic Bread', qty: 1, note: '' },
//       { name: 'Lemonade', qty: 2, note: '' },
//     ],
//   },
//   {
//     id: 'kot-3',
//     orderNumber: 1041,
//     table: 8,
//     section: 'Main Hall',
//     receivedAt: '14:35',
//     status: 'preparing',
//     items: [
//       { name: 'Caesar Salad', qty: 1, note: 'No croutons' },
//       { name: 'Grilled Salmon', qty: 1, note: 'Medium rare' },
//       { name: 'Sparkling Water', qty: 2, note: '' },
//     ],
//   },
//   {
//     id: 'kot-4',
//     orderNumber: 1040,
//     table: 3,
//     section: 'Bar',
//     receivedAt: '14:25',
//     status: 'preparing',
//     items: [
//       { name: 'Fish & Chips', qty: 2, note: 'Extra tartar' },
//       { name: 'Onion Rings', qty: 1, note: '' },
//     ],
//   },
//   {
//     id: 'kot-5',
//     orderNumber: 1039,
//     table: 7,
//     section: 'Main Hall',
//     receivedAt: '14:18',
//     status: 'ready',
//     items: [
//       { name: 'Tomato Soup', qty: 1, note: '' },
//       { name: 'Vegetable Rice', qty: 1, note: 'No spice' },
//       { name: 'Iced Tea', qty: 2, note: '' },
//     ],
//   },
//   {
//     id: 'kot-6',
//     orderNumber: 1038,
//     table: 11,
//     section: 'Terrace',
//     receivedAt: '14:12',
//     status: 'ready',
//     items: [
//       { name: 'Beef Steak', qty: 1, note: 'Medium' },
//       { name: 'Mashed Potato', qty: 1, note: '' },
//     ],
//   },
// ];

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-amber-500 text-white', borderColor: 'border-amber-500' },
  preparing: { label: 'Preparing', color: 'bg-blue-500 text-white', borderColor: 'border-blue-500' },
  ready: { label: 'Ready', color: 'bg-emerald-600 text-white', borderColor: 'border-emerald-600' },
};

// Stage map: display label -> API value
// API stages: "queued", "in_progress", "ready", "served"
// UI can also support custom stages if API allows them
const STAGE_MAP = [
  { value: 'queued', label: 'Queued' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'preparing', label: 'Preparing' }, // Support both API and UI naming
  { value: 'ready', label: 'Ready to Serve' },
  // Legacy/custom stages (if API supports them)
  { value: 'chopping', label: 'Chopping' },
  { value: 'mixing', label: 'Mixing Spices' },
  { value: 'garnishing', label: 'Garnishing' },
];

const getDisplayStatus = (status) => {
  const entry = STAGE_MAP.find((e) => e.value === status);
  return entry ? entry.label : 'Unknown';
};

// Map fine-grained stage to column style (new / preparing / ready) for filter/column logic
// API stages: "queued", "in_progress", "ready", "served"
const STAGE_TO_COLUMN = {
  queued: 'new',
  in_progress: 'preparing',
  preparing: 'preparing', // Support both API and UI naming
  ready: 'ready',
  served: 'ready',
  // Legacy support for custom stages (if API supports them)
  chopping: 'new',
  mixing: 'new',
  garnishing: 'preparing',
};

// Different color for each stage (card border + footer)
// API stages: "queued", "in_progress", "ready", "served"
const STAGE_COLOR_CONFIG = {
  queued: { color: 'bg-amber-500 text-white', borderColor: 'border-amber-500' },
  in_progress: { color: 'bg-blue-500 text-white', borderColor: 'border-blue-500' },
  preparing: { color: 'bg-blue-500 text-white', borderColor: 'border-blue-500' }, // Support both API and UI naming
  chopping: { color: 'bg-orange-500 text-white', borderColor: 'border-orange-500' },
  mixing: { color: 'bg-yellow-500 text-gray-900', borderColor: 'border-yellow-500' },
  garnishing: { color: 'bg-violet-500 text-white', borderColor: 'border-violet-500' },
  ready: { color: 'bg-emerald-600 text-white', borderColor: 'border-emerald-600' },
  served: { color: 'bg-gray-500 text-white', borderColor: 'border-gray-500' },
};

// Flatten orders to one entry per item (for one card per item)
const getOrderItemEntries = (orders) =>
  orders.flatMap((order) =>
    (order.items || []).map((item, idx) => ({
      order,
      item,
      itemId: item.order_item_id || `${order.id}-${idx}`,
    }))
  );

const filterItemEntriesBySearch = (itemEntries, query) => {
  if (!query || !String(query).trim()) return itemEntries;
  const q = String(query).trim().toLowerCase();
  return itemEntries.filter(
    ({ order }) =>
      String(order.orderNumber).toLowerCase().includes(q) ||
      String(order.table).toLowerCase().includes(q)
  );
};

// Dummy chefs (matches KitchenScreen.js – Flutter fetches via getActiveChefs(shiftId))
// const DUMMY_CHEFS = [
//   { user_id: 'chef-1', user_name: 'Chef Alex' },
//   { user_id: 'chef-2', user_name: 'Chef Sam' },
//   { user_id: 'chef-3', user_name: 'Chef Jordan' },
//   { user_id: 'chef-4', user_name: 'Chef Morgan' },
// ];

const selectClass = combineClasses(
  'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900',
  'focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400',
  typography.sh3
);

// Map KOT order status (new/preparing/ready) to stage value for card initial state
const orderStatusToStage = { new: 'queued', preparing: 'preparing', ready: 'ready' };

/** One card per item: shows order context + single item + stage/chef/remark dropdown (matches KitchenScreen OrderStatusCard) */
const KotItemCard = ({ order, item, itemId, chefs = [], onStageUpdate, shiftId }) => {
  // Map API status to UI status for initial state
  // API uses "in_progress", UI dropdown uses "preparing"
  const getInitialStage = (apiStatus) => {
    if (!apiStatus) return 'queued';
    const status = apiStatus.toLowerCase();
    // Map "in_progress" to "preparing" for UI dropdown
    return status === 'in_progress' ? 'preparing' : status;
  };
  
  const [selectedStage, setSelectedStage] = useState(getInitialStage(item.current_status));
  const [selectedChefId, setSelectedChefId] = useState(item.chef_assigned || '');
  const [chefRemarks, setChefRemarks] = useState(item.chef_remarks || '');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Get color config - handle both API and UI stage names
  const getStageColorConfig = (stage) => {
    // Map UI "preparing" to API "in_progress" for color lookup
    const apiStage = stage === 'preparing' ? 'in_progress' : stage;
    return STAGE_COLOR_CONFIG[apiStage] || STAGE_COLOR_CONFIG[stage] || STAGE_COLOR_CONFIG.queued;
  };
  
  const stageColorConfig = getStageColorConfig(selectedStage);

  const handleSetStage = async () => {
    if (isUpdating) return;

    const orderItemId = item.order_item_id || itemId;
    if (!orderItemId) {
      console.error('Order item ID is missing');
      return;
    }

    setIsUpdating(true);
    try {
      // Map UI stage names to API stage names (API uses lowercase, e.g., "in_progress")
      const apiStageName = selectedStage === 'preparing' ? 'in_progress' : selectedStage.toLowerCase();

      if (apiStageName === 'ready') {
        // For "ready" stage, only order_item_id and stage_name are required (no operation)
        await restaurantApi.setOrderItemStage({
          order_item_id: orderItemId,
          stage_name: apiStageName,
        });
        
        // Log API event for setting order item stage to ready
        loggingService.logInfo(
          'order_item_stage_set',
          `Set order item ${orderItemId} stage to ready`,
          {
            endpoint: '/restaurant/kitchen/set-stage',
            method: 'POST',
            orderItemId: orderItemId,
            stage: apiStageName,
          }
        ).catch(logError => {
          console.warn('[KitchenDisplayScreen] Failed to log event:', logError);
        });
        
        // Send ready notification
        await restaurantApi.sendReadyNotification(orderItemId);
        
        // Log API event for sending ready notification
        loggingService.logInfo(
          'order_item_ready_notification',
          `Sent ready notification for order item ${orderItemId}`,
          {
            endpoint: '/restaurant/send-ready-notification',
            method: 'POST',
            orderItemId: orderItemId,
          }
        ).catch(logError => {
          console.warn('[KitchenDisplayScreen] Failed to log event:', logError);
        });
        
        // Refresh orders after update
        onStageUpdate?.();
      } else {
        // End current stage if not queued
        // item.current_status is from API (e.g., "in_progress", "queued", "ready")
        if (item.current_status && item.current_status !== 'queued') {
          // Use the API stage name directly (already lowercase from transformOrders)
          const currentApiStage = item.current_status.toLowerCase();
          await restaurantApi.setOrderItemStage({
            order_item_id: orderItemId,
            stage_name: currentApiStage,
            operation: 'end',
            chef_remarks: chefRemarks,
          });
          
          // Log API event for ending order item stage
          loggingService.logInfo(
            'order_item_stage_end',
            `Ended order item ${orderItemId} stage ${currentApiStage}`,
            {
              endpoint: '/restaurant/kitchen/set-stage',
              method: 'POST',
              orderItemId: orderItemId,
              stage: currentApiStage,
              operation: 'end',
              hasChefRemarks: !!chefRemarks,
            }
          ).catch(logError => {
            console.warn('[KitchenDisplayScreen] Failed to log event:', logError);
          });
        }

        // Start new stage with chef assignment
        // For non-queued stages, chef_assigned is required
        if (apiStageName !== 'queued' && (!selectedChefId || selectedChefId === '')) {
          alert('Please assign a chef for the new stage');
          setIsUpdating(false);
          return;
        }

        const startPayload = {
          order_item_id: orderItemId,
          stage_name: apiStageName,
          operation: 'start',
        };

        // Add chef_assigned only for non-queued stages
        if (apiStageName !== 'queued') {
          startPayload.chef_assigned = selectedChefId;
        }

        await restaurantApi.setOrderItemStage(startPayload);
        
        // Log API event for starting order item stage
        loggingService.logInfo(
          'order_item_stage_start',
          `Started order item ${orderItemId} stage ${apiStageName}`,
          {
            endpoint: '/restaurant/kitchen/set-stage',
            method: 'POST',
            orderItemId: orderItemId,
            stage: apiStageName,
            operation: 'start',
            chefAssigned: selectedChefId || null,
          }
        ).catch(logError => {
          console.warn('[KitchenDisplayScreen] Failed to log event:', logError);
        });
        
        // Refresh orders after update
        onStageUpdate?.();
      }
      setDropdownOpen(false);
    } catch (error) {
      console.error('Error updating stage:', error);
      alert(`Failed to update stage: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card variant="solid" className={combineClasses('overflow-hidden border-l-4 flex flex-col', stageColorConfig.borderColor)}>
      <div className="px-4 py-2 flex items-center justify-between border-b border-gray-200">
        <div className="flex items-baseline gap-2">
          <span className={combineClasses('text-gray-900', typography.h4)}>#{order.orderNumber || order.id}</span>
          <span className={combineClasses('text-gray-600', typography.sh3)}>
            {order.table ? `T${order.table}` : ''}{order.section ? ` · ${order.section}` : ''}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-gray-600">
          <Clock className="w-4 h-4" aria-hidden />
          <span className={combineClasses(typography.sh3)}>{order.receivedAt}</span>
        </div>
      </div>
      <div className="px-4 py-3 flex-1">
        {/* Single item for this card */}
        <div className="flex flex-wrap items-baseline gap-2">
          <span className={combineClasses('font-semibold text-gray-900', typography.sh2)}>
            {item.qty > 1 ? `${item.qty}× ` : ''}{item.name}
          </span>
          {item.note ? (
            <span className={combineClasses('text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded text-sm', typography.sh3)}>
              {item.note}
            </span>
          ) : null}
        </div>

        {/* Dropdown: Select stage, Assigned chef, Chef remark */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={() => setDropdownOpen((prev) => !prev)}
            className={combineClasses(
              'w-full flex items-center justify-between gap-2 rounded-lg px-3 py-2',
              'text-left text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors',
              typography.sh3
            )}
            aria-expanded={dropdownOpen}
            aria-controls={`kot-dropdown-${itemId}`}
            id={`kot-dropdown-trigger-${itemId}`}
          >
            <span>Set Stage</span>
            {dropdownOpen ? (
              <ChevronUp className="w-4 h-4 shrink-0" aria-hidden />
            ) : (
              <ChevronDown className="w-4 h-4 shrink-0" aria-hidden />
            )}
          </button>
          {dropdownOpen && (
            <div
              id={`kot-dropdown-${itemId}`}
              role="region"
              aria-labelledby={`kot-dropdown-trigger-${itemId}`}
              className="mt-2 space-y-2"
            >
              <div>
                <label className={combineClasses('block text-gray-600 mb-1', typography.sh3)}>
                  Select Stage
                </label>
                <select
                  value={selectedStage}
                  onChange={(e) => setSelectedStage(e.target.value)}
                  className={selectClass}
                  aria-label="Select stage"
                >
                  {STAGE_MAP.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {selectedStage !== 'ready' && (
                <div>
                  <label className={combineClasses('block text-gray-600 mb-1', typography.sh3)}>
                    Assign Chef
                  </label>
                  <select
                    value={selectedChefId}
                    onChange={(e) => setSelectedChefId(e.target.value)}
                    className={selectClass}
                    aria-label="Assign chef"
                  >
                    <option value="">Select chef…</option>
                    {chefs.map((c) => (
                      <option key={c.user_id} value={c.user_id}>
                        {c.name || c.user_name || 'Unknown Chef'}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className={combineClasses('block text-gray-600 mb-1', typography.sh3)}>
                  Chef Remarks
                </label>
                <Input
                  value={chefRemarks}
                  onChange={(e) => setChefRemarks(e.target.value)}
                  placeholder="Enter any remarks about the dish"
                  className="!bg-white !border-gray-200 !text-gray-900 placeholder:!text-gray-400 py-2 rounded-lg"
                />
              </div>
              <div className="pt-1">
                <PrimaryButton
                  type="button"
                  onClick={handleSetStage}
                  disabled={isUpdating}
                  className="w-full !py-2 !h-auto text-base"
                >
                  {isUpdating ? 'Updating...' : 'Set Stage'}
                </PrimaryButton>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={combineClasses('px-4 py-2 rounded-b-lg text-center text-sm font-semibold', stageColorConfig.color, typography.sh3)}>
        {getDisplayStatus(selectedStage)}
      </div>
    </Card>
  );
};

const KitchenDisplayScreen = () => {
  // Log screen view
  useScreenLogging('KitchenDisplayScreen', {
    component: 'KitchenDisplayScreen',
    screenType: 'kitchen-display',
  });

  const [filterStatus, setFilterStatus] = useState(null);
  const [columnSearch, setColumnSearch] = useState({ new: '', 'preparing-1': '', 'preparing-2': '' /* ready: '' */ });
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [isSitesLoading, setIsSitesLoading] = useState(true);
  const [sitesError, setSitesError] = useState(null);
  const [shiftId, setShiftId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [chefs, setChefs] = useState([]);
  const [dishCache, setDishCache] = useState({});
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState(null);
  const refreshIntervalRef = useRef(null);

  // Fetch sites on component mount
  useEffect(() => {
    const fetchSites = async () => {
      setIsSitesLoading(true);
      setSitesError(null);
      try {
        const data = await getAllSites();
        const list = Array.isArray(data?.sites)
          ? data.sites
          : Array.isArray(data)
          ? data
          : [];
        const sorted = [...list].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        setSites(sorted);
        // Auto-select first site if available
        if (sorted.length > 0 && !selectedSite) {
          const firstSite = sorted[0];
          setSelectedSite(firstSite);
        }
      } catch (error) {
        console.error('Error fetching sites:', error);
        setSitesError(error?.message || 'Failed to load sites. Please refresh the page.');
      } finally {
        setIsSitesLoading(false);
      }
    };

    fetchSites();
  }, []);

  // Fetch current shift when site changes
  useEffect(() => {
    const fetchShift = async () => {
      if (!selectedSite) {
        setShiftId(null);
        setOrders([]);
        return;
      }

      const siteId = selectedSite.site_id || selectedSite.id || selectedSite.siteId;
      if (!siteId) {
        console.error('Site ID not found');
        return;
      }

      try {
        const shiftData = await restaurantApi.fetchCurrentShiftForSite(siteId);
        const ssId = shiftData?.ss_id || shiftData?.shift_id || shiftData?.shift_schedule_id;
        if (ssId) {
          setShiftId(ssId);
          
          // Log API event for fetching current shift
          loggingService.logInfo(
            'shift_current_fetch',
            `Fetched current shift for site ${siteId}`,
            {
              endpoint: '/shift/current-for-site',
              method: 'GET',
              siteId: siteId,
              shiftScheduleId: ssId,
            }
          ).catch(logError => {
            console.warn('[KitchenDisplayScreen] Failed to log event:', logError);
          });
        } else {
          console.warn('No active shift found for site');
          setShiftId(null);
          setOrders([]);
        }
      } catch (error) {
        console.error('Error fetching shift:', error);
        
        // Handle CORS errors with user-friendly message
        if (error.isCorsError) {
          console.error(
            'CORS Error: The API endpoint is not configured to allow requests from this origin. ' +
            'Please contact the backend team to configure CORS headers for the Restaurant API endpoints.'
          );
          // Set error state to show in UI
          setOrdersError(
            'CORS Error: Unable to connect to the API. Please check backend CORS configuration or contact support.'
          );
        }
        
        setShiftId(null);
        setOrders([]);
      }
    };

    fetchShift();
  }, [selectedSite]);

  // Fetch active chefs when shift is available
  useEffect(() => {
    const fetchChefs = async () => {
      if (!shiftId) {
        setChefs([]);
        return;
      }

      try {
        const chefsData = await restaurantApi.getActiveChefs(shiftId);
        const chefsList = Array.isArray(chefsData) ? chefsData : chefsData?.chefs || [];
        setChefs(chefsList);
        
        // Log API event for fetching active chefs
        loggingService.logInfo(
          'chefs_active_fetch',
          `Fetched ${chefsList.length} active chefs for shift ${shiftId}`,
          {
            endpoint: '/restaurant/kitchen/get-active-chefs',
            method: 'GET',
            shiftScheduleId: shiftId,
            chefCount: chefsList.length,
          }
        ).catch(logError => {
          console.warn('[KitchenDisplayScreen] Failed to log event:', logError);
        });
      } catch (error) {
        console.error('Error fetching chefs:', error);
        setChefs([]);
      }
    };

    fetchChefs();
  }, [shiftId]);

  // Fetch dish name and cache it
  const getDishName = useCallback(async (dishId) => {
    if (!dishId) return 'Unknown Dish';
    if (dishCache[dishId]) return dishCache[dishId];

    try {
      const dishData = await restaurantApi.getDish(dishId);
      const dishName = dishData?.dish_name || dishData?.name || 'Unknown Dish';
      setDishCache((prev) => ({ ...prev, [dishId]: dishName }));
      
      // Log API event for fetching dish (only log once per dish to avoid spam)
      if (!dishCache[dishId]) {
        loggingService.logInfo(
          'dish_fetch',
          `Fetched dish ${dishId}`,
          {
            endpoint: '/restaurant/dish',
            method: 'GET',
            dishId: dishId,
            dishName: dishName,
          }
        ).catch(logError => {
          console.warn('[KitchenDisplayScreen] Failed to log event:', logError);
        });
      }
      
      return dishName;
    } catch (error) {
      console.error('Error fetching dish:', error);
      return 'Unknown Dish';
    }
  }, [dishCache]);

  // Transform API orders to component format
  const transformOrders = useCallback(async (apiOrders) => {
    if (!Array.isArray(apiOrders)) return [];

    const transformed = await Promise.all(
      apiOrders.map(async (apiOrder) => {
        const orderItems = await Promise.all(
          (apiOrder.order_items || []).map(async (item) => {
            const dishName = await getDishName(item.dish_id);
            const currentStatus = item.current_status?.toLowerCase() || 'queued';
            
            // Extract chef_assigned and chef_remarks from stages object if available
            // API structure: stages[stage_name].chef_assigned or item.chef_assigned
            let chefAssigned = item.chef_assigned || '';
            let chefRemarks = item.chef_remarks || '';
            
            // Check stages object for current stage's chef info
            if (item.stages && item.stages[currentStatus]) {
              const stageData = item.stages[currentStatus];
              chefAssigned = chefAssigned || stageData.chef_assigned || '';
              chefRemarks = chefRemarks || stageData.chef_remarks || '';
            }
            
            return {
              order_item_id: item.order_item_id,
              dish_id: item.dish_id,
              name: dishName,
              qty: item.quantity || item.qty || 1,
              note: item.cust_mods || item.note || '',
              current_status: currentStatus,
              chef_assigned: chefAssigned,
              chef_remarks: chefRemarks,
            };
          })
        );

        // Map order status based on items
        // API returns: "queued", "in_progress", "ready", "served"
        // Map to UI status: "new", "preparing", "ready"
        const hasActiveItems = orderItems.some((item) => {
          const status = item.current_status?.toLowerCase();
          return status !== 'ready' && status !== 'served';
        });
        const status = hasActiveItems
          ? orderItems.some((item) => {
              const itemStatus = item.current_status?.toLowerCase();
              return itemStatus === 'in_progress' || itemStatus === 'preparing';
            })
            ? 'preparing'
            : 'new'
          : 'ready';

        return {
          id: apiOrder.order_id,
          orderNumber: apiOrder.order_number || apiOrder.orderNumber || apiOrder.order_id,
          table: apiOrder.table_no || apiOrder.table || '',
          section: apiOrder.section || apiOrder.area || '',
          receivedAt: formatReceivedAt(apiOrder.received_at || apiOrder.receivedAt),
          status: status,
          items: orderItems,
        };
      })
    );

    return transformed;
  }, [getDishName]);

  // Fetch active orders
  const fetchActiveOrders = useCallback(async () => {
    if (!shiftId) {
      setOrders([]);
      return;
    }

    setIsLoadingOrders(true);
    setOrdersError(null);
    try {
      const apiOrders = await restaurantApi.getActiveOrders(shiftId);
      const ordersList = Array.isArray(apiOrders) ? apiOrders : apiOrders?.orders || [];
      const transformed = await transformOrders(ordersList);
      setOrders(transformed);
      
      // Log API event for fetching active orders
      loggingService.logInfo(
        'orders_active_fetch',
        `Fetched ${ordersList.length} active orders for shift ${shiftId}`,
        {
          endpoint: '/restaurant/order/get-active',
          method: 'GET',
          shiftScheduleId: shiftId,
          orderCount: ordersList.length,
        }
      ).catch(logError => {
        console.warn('[KitchenDisplayScreen] Failed to log event:', logError);
      });
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrdersError(error?.message || 'Failed to load orders. Please refresh.');
      setOrders([]);
    } finally {
      setIsLoadingOrders(false);
    }
  }, [shiftId, transformOrders]);

  // Fetch orders when shift changes
  useEffect(() => {
    fetchActiveOrders();
  }, [fetchActiveOrders]);

  // Auto-refresh orders every 30 seconds
  useEffect(() => {
    if (shiftId) {
      refreshIntervalRef.current = setInterval(() => {
        fetchActiveOrders();
      }, 30000); // 30 seconds

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [shiftId, fetchActiveOrders]);

  const handleColumnSearchChange = (stateKey, e) => {
    setColumnSearch((prev) => ({ ...prev, [stateKey]: e.target.value }));
  };

  const handleSiteChange = (e) => {
    const siteId = e.target.value;
    const site = sites.find(
      s => (s.site_id || s.id || s.siteId) === siteId
    );
    setSelectedSite(site || null);
  };

  const resolveSiteValue = (site) => {
    return site?.site_id || site?.id || site?.siteId || '';
  };

  const ordersByStatus = {
    new: orders.filter((o) => o.status === 'new'),
    preparing: orders.filter((o) => o.status === 'preparing'),
    ready: orders.filter((o) => o.status === 'ready'), // kept for when Ready column is uncommented
  };

  // Preparing split into 2 columns (first half / second half of preparing orders)
  const preparingOrders = ordersByStatus.preparing;
  const preparingMid = Math.ceil(preparingOrders.length / 2);
  const preparingOrdersCol1 = preparingOrders.slice(0, preparingMid);
  const preparingOrdersCol2 = preparingOrders.slice(preparingMid);

  const columns = [
    { key: 'new', label: 'New Orders', orders: ordersByStatus.new },
    { key: 'preparing-1', label: 'Preparing', orders: preparingOrdersCol1 },
    { key: 'preparing-2', label: 'Preparing', orders: preparingOrdersCol2 },
    // { key: 'ready', label: 'Ready', orders: ordersByStatus.ready }, // Ready – commented out, don't display
  ];

  const displayColumns = filterStatus
    ? filterStatus === 'preparing'
      ? columns.filter((c) => c.key === 'preparing-1' || c.key === 'preparing-2')
      : columns.filter((c) => c.key === filterStatus)
    : columns;

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {/* <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-800 text-white">
            <Utensils className="h-6 w-6" aria-hidden />
          </div> */}
          <div>
            <h1 className={combineClasses('text-gray-900', typography.h2)}>
              Kitchen Order Ticket
            </h1>
            <p className={combineClasses('text-gray-600', typography.sh3)}>
              KOT Board
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {/* Site Selection Dropdown */}
          <div className="min-w-[200px]">
            <label
              htmlFor="kitchen-site"
              className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
            >
              <MapPin className="w-4 h-4" />
              Select Site
            </label>
            <select
              id="kitchen-site"
              value={selectedSite ? resolveSiteValue(selectedSite) : ''}
              onChange={handleSiteChange}
              className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
              disabled={isSitesLoading}
            >
              <option value="">
                {isSitesLoading ? 'Loading sites...' : 'Select a site'}
              </option>
              {sites.map((site) => (
                <option key={resolveSiteValue(site)} value={resolveSiteValue(site)}>
                  {site.site_name || site.name || 'Unknown Site'}
                </option>
              ))}
            </select>
            {sitesError && (
              <p className="mt-1 text-xs text-red-600">{sitesError}</p>
            )}
          </div>
          {/* Filter Buttons */}
          <div className="flex gap-2">
            {['new', 'preparing'].map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilterStatus(filterStatus === key ? null : key)}
                className={combineClasses(
                  'rounded-lg px-4 py-2 font-semibold transition-colors',
                  typography.sh3,
                  filterStatus === key
                    ? STATUS_CONFIG[key].color
                    : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                )}
              >
                {STATUS_CONFIG[key].label}
              </button>
            ))}
            {/* Ready – commented out, don't display
            <button ... onClick={() => setFilterStatus(filterStatus === 'ready' ? null : 'ready')} ...>
              {STATUS_CONFIG['ready'].label}
            </button>
            */}
          </div>
        </div>
      </header>

      {/* KOT columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {displayColumns.map((col) => (
          <div key={col.key} className="flex flex-col">
            <div
              className={combineClasses(
                'mb-3 rounded-lg px-3 py-3 flex flex-col gap-2 min-w-0',
                col.key === 'new' && 'bg-amber-500',
                (col.key === 'preparing-1' || col.key === 'preparing-2') && 'bg-blue-500',
                // col.key === 'ready' && 'bg-emerald-600' // Ready – commented out
              )}
            >
              <div className={combineClasses('flex items-center gap-2 text-white', typography.sh2)}>
                <span>{col.label}</span>
                <span className="ml-auto rounded-full bg-white/20 px-2 py-0.5 text-sm">
                  {getOrderItemEntries(col.orders).length}
                </span>
              </div>
              <div className="w-full min-w-0 overflow-hidden">
                <SearchBar
                  placeholder="Search order or table number…"
                  value={columnSearch[col.key] ?? ''}
                  onChange={(e) => handleColumnSearchChange(col.key, e)}
                  iconType="search"
                  className={combineClasses(
                    'w-full h-11 !bg-white/20 !border-white/30 !outline-white/50 !shadow-none',
                    'placeholder:!text-white/80 [&_input]:!text-white [&_input]:placeholder:!text-white/80',
                    '[&_input]:overflow-hidden [&_input]:text-ellipsis [&_input]:whitespace-nowrap [&_input]:min-w-0',
                    '[&_svg]:!text-white/80 [&>div]:min-w-0'
                  )}
                />
              </div>
            </div>
            <div className="flex flex-col gap-4 overflow-auto">
              {isLoadingOrders && col.key === 'new' ? (
                <Card variant="light" className="flex-1 p-6 text-center">
                  <p className={combineClasses('text-gray-500', typography.p)}>
                    Loading orders...
                  </p>
                </Card>
              ) : ordersError && col.key === 'new' ? (
                <Card variant="light" className="flex-1 p-6 text-center">
                  <p className={combineClasses('text-red-600', typography.p)}>
                    {ordersError}
                  </p>
                </Card>
              ) : (() => {
                const itemEntries = getOrderItemEntries(col.orders);
                const filteredItems = filterItemEntriesBySearch(itemEntries, columnSearch[col.key]);
                if (filteredItems.length === 0) {
                  return (
                    <Card variant="light" className="flex-1 p-6 text-center">
                      <p className={combineClasses('text-gray-500', typography.p)}>
                        {itemEntries.length === 0
                          ? 'No items in this column'
                          : 'No items match your search'}
                      </p>
                    </Card>
                  );
                }
                return filteredItems.map(({ order, item, itemId }) => (
                  <KotItemCard
                    key={itemId}
                    order={order}
                    item={item}
                    itemId={itemId}
                    chefs={chefs}
                    shiftId={shiftId}
                    onStageUpdate={fetchActiveOrders}
                  />
                ));
              })()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default KitchenDisplayScreen;
